import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { ApiError } from '../../identity/types';

export const MFA_NEEDED_MESSAGE =
  'This action needs a recent authenticator-app check. Sign out and sign in again with your authenticator code, then try once more. Nothing was changed.';

export interface Failure {
  message: string;
  /** The server needs a recent authenticator check (403 `MFA_REQUIRED`). Nothing was saved; what was typed is kept. */
  mfaRequired: boolean;
  /** The request may or may not have reached the server (a network failure or a 5xx). A retry of a create must reuse its idempotency key. */
  outcomeUnknown: boolean;
}

/**
 * Turns a failed call into words a person can act on. The server's own wording is used when it wrote one for a refusal it expects (400 and 409 carry the rule that
 * was broken); a permission, session, subscription or service problem gets the app's standard sentence. Never a stack trace, a path or a raw status line.
 */
export function describeFailure(err: unknown, fallback: string): Failure {
  if (err instanceof ApiError) {
    if (err.status === 403 && err.code === 'MFA_REQUIRED') return { message: MFA_NEEDED_MESSAGE, mfaRequired: true, outcomeUnknown: false };
    if (err.status === 404) return { message: 'This payment could not be found. Close this window, refresh the dashboard and try again.', mfaRequired: false, outcomeUnknown: false };
    if (err.status === 403 && err.code === null) {
      return { message: 'You don’t have permission to do this. Ask an administrator to grant you access.', mfaRequired: false, outcomeUnknown: false };
    }
    return { message: friendlyErrorMessage(err, fallback), mfaRequired: false, outcomeUnknown: err.status >= 500 };
  }
  return { message: 'The server could not be reached. Nothing is confirmed as saved. Check your connection and try again.', mfaRequired: false, outcomeUnknown: true };
}

/** A one-per-attempt key for the server's duplicate guard (8 to 100 letters, digits, dots, dashes, colons or underscores). */
export function newIdempotencyKey(): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `epn-${random}`;
}
